/**
 * PlayerProfileBadge — Reusable player avatar component
 *
 * Sizes:
 *   'mini' — 24px, used in stacked rows (room cells, lobby seats)
 *   'card' — 140px column card with name + division pill + streak (showcase)
 *   'row'  — horizontal cell with avatar + username + rank pill (player lists)
 *
 * Reused by:
 *   - PlayerShowcase (card)
 *   - RoomCell stack (mini)
 *   - PlayersGridOverlay (row)
 *   - Future: in-game seats, leaderboards, friend list
 *
 * Tap emits 'tap' — caller decides what to do (e.g. open PlayerCard modal).
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';

export type PlayerProfileBadgeSize = 'mini' | 'card' | 'row';

export type RingColor = 'gold' | 'cyan' | 'magenta' | 'purple' | 'green' | 'silver';

export type PlayerStatus = 'online' | 'busy' | 'idle';

export interface PlayerProfileData {
  id: string;
  username: string;
  ringColor: RingColor;
  picture?: string;
  status?: PlayerStatus;
  /** Card-only: short division label rendered as a pill (e.g. 'DIAMOND'). */
  division?: string;
  /** Card-only: streak/stat line below division. */
  streak?: string;
  /** Card-only: render streak muted (cold streak / rookie). */
  streakCold?: boolean;
  /** Card-only: pre-formatted bank balance (e.g. "2.59M") for the flex line. */
  bank?: string;
}

interface PlayerProfileBadgeEvents {
  'tap': { playerId: string };
}

export class PlayerProfileBadge extends EventEmitter<PlayerProfileBadgeEvents> {
  public element: HTMLElement;
  private data: PlayerProfileData;
  private size: PlayerProfileBadgeSize;

  constructor(data: PlayerProfileData, size: PlayerProfileBadgeSize = 'card') {
    super();
    this.data = data;
    this.size = size;
    this.element = document.createElement(size === 'mini' ? 'div' : 'button');
    this.element.className = `sdl-pbadge sdl-pbadge-${size}`;
    this.element.dataset.playerId = data.id;
    this.element.addEventListener('click', () => this.emit('tap', { playerId: this.data.id }));

    PlayerProfileBadge.injectStyles();
    this.render();
  }

  update(patch: Partial<PlayerProfileData>): void {
    const prev = this.data;
    this.data = { ...prev, ...patch };
    if (patch.id !== undefined) this.element.dataset.playerId = this.data.id;
    this.render();
  }

  getData(): PlayerProfileData {
    return this.data;
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  /** Render an empty seat in the same visual rhythm as a mini badge. */
  static createEmptySlot(): HTMLElement {
    PlayerProfileBadge.injectStyles();
    const el = document.createElement('div');
    el.className = 'sdl-pbadge sdl-pbadge-mini sdl-pbadge-empty';
    el.innerHTML = '<span>+</span>';
    return el;
  }

  private render(): void {
    if (this.size === 'mini') this.renderMini();
    else if (this.size === 'row') this.renderRow();
    else this.renderCard();
  }

  private renderMini(): void {
    const { ringColor, picture, username } = this.data;
    const initials = this.getInitials();
    const inner = picture
      ? `<img src="${picture}" alt="${escapeHtml(username)}" />`
      : `<span>${escapeHtml(initials)}</span>`;
    this.element.dataset.ring = ringColor;
    this.element.innerHTML = inner;
  }

  private renderRow(): void {
    const { ringColor, picture, username, status, division, streak, streakCold } = this.data;
    const initials = this.getInitials();
    const avatarInner = picture
      ? `<img src="${picture}" alt="${escapeHtml(username)}" />`
      : `<span>${escapeHtml(initials)}</span>`;
    const dot = status
      ? `<span class="sdl-pbadge-dot" data-status="${status}"></span>`
      : '';
    const divisionPill = division
      ? `<span class="sdl-pbadge-pill" data-ring="${ringColor}">${escapeHtml(division)}</span>`
      : '';
    const streakLine = streak
      ? `<div class="sdl-pbadge-row-sub${streakCold ? ' cold' : ''}">${escapeHtml(streak)}</div>`
      : '';

    this.element.innerHTML = `
      <div class="sdl-pbadge-avatar-wrap">
        <div class="sdl-pbadge-ring" data-ring="${ringColor}"></div>
        <div class="sdl-pbadge-avatar" data-ring="${ringColor}">${avatarInner}</div>
        ${dot}
      </div>
      <div class="sdl-pbadge-row-text">
        <div class="sdl-pbadge-row-name">${escapeHtml(username)}</div>
        ${streakLine}
      </div>
      ${divisionPill}
    `;
  }

  private renderCard(): void {
    const { ringColor, picture, username, status, division, streak, streakCold, bank } = this.data;
    const initials = this.getInitials();
    const avatarInner = picture
      ? `<img src="${picture}" alt="${escapeHtml(username)}" />`
      : `<span>${escapeHtml(initials)}</span>`;
    const dot = status
      ? `<span class="sdl-pbadge-dot" data-status="${status}"></span>`
      : '';
    const divisionPill = division
      ? `<span class="sdl-pbadge-pill" data-ring="${ringColor}">${escapeHtml(division)}</span>`
      : '';
    const bankLine = bank
      ? `<div class="sdl-pbadge-bank">
           <img class="sdl-pbadge-bank-icon" src="/assets/icons/currency/stacks.svg" alt="" />
           <span class="sdl-pbadge-bank-amt">${escapeHtml(bank)}</span>
         </div>`
      : '';
    /* Always render the streak slot so cards with and without a streak keep
       the same height in the showcase grid; the empty one is just invisible. */
    const streakLine = streak
      ? `<div class="sdl-pbadge-streak${streakCold ? ' cold' : ''}">${escapeHtml(streak)}</div>`
      : `<div class="sdl-pbadge-streak sdl-pbadge-streak-empty">&nbsp;</div>`;

    this.element.innerHTML = `
      <div class="sdl-pbadge-avatar-wrap">
        <div class="sdl-pbadge-ring" data-ring="${ringColor}"></div>
        <div class="sdl-pbadge-avatar" data-ring="${ringColor}">${avatarInner}</div>
        ${dot}
      </div>
      <div class="sdl-pbadge-name">${escapeHtml(username)}</div>
      ${divisionPill ? `<div class="sdl-pbadge-meta">${divisionPill}</div>` : ''}
      ${bankLine}
      ${streakLine}
    `;
  }

  private getInitials(): string {
    const { username } = this.data;
    const parts = username.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return username.slice(0, 2).toUpperCase();
  }

  // ---------- Styles (injected once) ----------

  private static stylesInjected = false;

  private static injectStyles(): void {
    if (PlayerProfileBadge.stylesInjected) return;
    if (document.getElementById('sdl-pbadge-styles')) {
      PlayerProfileBadge.stylesInjected = true;
      return;
    }
    const style = document.createElement('style');
    style.id = 'sdl-pbadge-styles';
    style.textContent = PBADGE_STYLES;
    document.head.appendChild(style);
    PlayerProfileBadge.stylesInjected = true;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({
    '<': '&lt;',
    '>': '&gt;',
    '&': '&amp;',
    '"': '&quot;',
    "'": '&#39;',
  }[c] || c));
}

const RING_GRADIENTS = `
  .sdl-pbadge-ring[data-ring="gold"], .sdl-pbadge-pill[data-ring="gold"] { --ring: var(--gold); --ring-dim: var(--gold-dim); }
  .sdl-pbadge-ring[data-ring="cyan"], .sdl-pbadge-pill[data-ring="cyan"] { --ring: var(--cyan); --ring-dim: var(--cyan-dim); }
  .sdl-pbadge-ring[data-ring="magenta"], .sdl-pbadge-pill[data-ring="magenta"] { --ring: var(--magenta); --ring-dim: var(--magenta-dim); }
  .sdl-pbadge-ring[data-ring="purple"], .sdl-pbadge-pill[data-ring="purple"] { --ring: var(--purple); --ring-dim: var(--purple-dim); }
  .sdl-pbadge-ring[data-ring="green"], .sdl-pbadge-pill[data-ring="green"] { --ring: var(--green); --ring-dim: var(--green-dim); }
  .sdl-pbadge-ring[data-ring="silver"], .sdl-pbadge-pill[data-ring="silver"] { --ring: #d8d8e0; --ring-dim: #6a6a78; }
  .sdl-pbadge-avatar[data-ring="gold"] { color: var(--gold); }
  .sdl-pbadge-avatar[data-ring="cyan"] { color: var(--cyan); }
  .sdl-pbadge-avatar[data-ring="magenta"] { color: var(--magenta); }
  .sdl-pbadge-avatar[data-ring="purple"] { color: var(--purple); }
  .sdl-pbadge-avatar[data-ring="green"] { color: var(--green); }
  .sdl-pbadge-avatar[data-ring="silver"] { color: #fff; }
`;

const PBADGE_STYLES = `
  .sdl-pbadge { all: unset; box-sizing: border-box; cursor: pointer; }
  .sdl-pbadge-card { display: block; }

  /* ---------- CARD VARIANT ---------- */
  .sdl-pbadge-card {
    flex: 0 0 140px;
    scroll-snap-align: start;
    background: linear-gradient(180deg, var(--concrete-light) 0%, var(--concrete) 100%);
    border: 1px solid var(--glass-border);
    border-radius: 16px;
    padding: 14px 12px 12px;
    transition: transform 0.18s ease, border-color 0.18s ease;
    text-align: center;
  }
  .sdl-pbadge-card:active { transform: scale(0.96); }

  .sdl-pbadge-avatar-wrap {
    position: relative;
    width: 64px; height: 64px;
    margin: 4px auto 10px;
  }
  .sdl-pbadge-ring {
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    background: conic-gradient(from 0deg, var(--ring), var(--ring-dim), var(--ring), transparent, var(--ring));
    animation: sdl-pbadge-ring-rotate 6s linear infinite;
  }
  @keyframes sdl-pbadge-ring-rotate { to { transform: rotate(360deg); } }
  .sdl-pbadge-avatar {
    position: relative;
    width: 100%; height: 100%;
    border-radius: 50%;
    background: linear-gradient(135deg, #2a2a35, #15151a);
    display: flex; align-items: center; justify-content: center;
    font-family: var(--font-display);
    font-size: 28px;
    letter-spacing: 1px;
    border: 3px solid var(--concrete);
    overflow: hidden;
  }
  .sdl-pbadge-avatar img {
    width: 100%; height: 100%; object-fit: cover;
    border-radius: 50%;
  }
  .sdl-pbadge-dot {
    position: absolute;
    bottom: 2px; right: 2px;
    width: 14px; height: 14px;
    border-radius: 50%;
    border: 2px solid var(--concrete);
  }
  .sdl-pbadge-dot[data-status="online"] { background: var(--green); box-shadow: 0 0 6px var(--green); }
  .sdl-pbadge-dot[data-status="busy"]   { background: var(--red); box-shadow: 0 0 6px var(--red); }
  .sdl-pbadge-dot[data-status="idle"]   { background: var(--orange); }

  .sdl-pbadge-name {
    font-family: var(--font-display);
    font-size: 16px;
    letter-spacing: 1.5px;
    color: white;
    line-height: 1.05;
    /* Single line on the narrow showcase card — long / special-character names
       truncate with an ellipsis instead of wrapping and breaking the grid. The
       full handle is visible on the profile card when tapped. */
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sdl-pbadge-meta {
    display: flex; justify-content: center;
    margin-top: 6px;
  }
  /* Bank flex line — the showcase doubles as a "show off your stack" board. */
  .sdl-pbadge-bank {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 8px;
  }
  .sdl-pbadge-bank-icon {
    width: 14px; height: 14px;
    flex-shrink: 0;
  }
  .sdl-pbadge-bank-amt {
    font-family: var(--font-display);
    font-size: 17px;
    letter-spacing: 0.5px;
    line-height: 1;
    color: var(--green);
    text-shadow: 0 0 10px color-mix(in srgb, var(--green) 35%, transparent);
  }
  .sdl-pbadge-pill {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1px;
    background: color-mix(in srgb, var(--ring) 12%, transparent);
    color: var(--ring);
    border: 1px solid color-mix(in srgb, var(--ring) 25%, transparent);
  }
  .sdl-pbadge-streak {
    margin-top: 8px;
    padding: 4px 0;
    font-family: var(--font-body);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.8px;
    background: rgba(255, 215, 0, 0.06);
    border-radius: 6px;
    color: var(--gold);
  }
  .sdl-pbadge-streak.cold {
    background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.45);
  }
  .sdl-pbadge-streak-empty { visibility: hidden; }

  /* ---------- MINI VARIANT (avatar stack) ---------- */
  .sdl-pbadge-mini {
    width: 24px; height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2a2a35, #15151a);
    display: inline-flex; align-items: center; justify-content: center;
    font-family: var(--font-display);
    font-size: 9px;
    letter-spacing: 0.3px;
    color: white;
    border: 1.5px solid var(--gold);
    box-shadow: 0 0 0 2px var(--concrete-light);
    position: relative;
    flex-shrink: 0;
    overflow: hidden;
  }
  .sdl-pbadge-mini img {
    width: 100%; height: 100%; object-fit: cover;
    border-radius: 50%;
  }
  .sdl-pbadge-mini[data-ring="gold"]     { border-color: var(--gold);     color: var(--gold); }
  .sdl-pbadge-mini[data-ring="cyan"]     { border-color: var(--cyan);     color: var(--cyan); }
  .sdl-pbadge-mini[data-ring="magenta"]  { border-color: var(--magenta);  color: var(--magenta); }
  .sdl-pbadge-mini[data-ring="purple"]   { border-color: var(--purple);   color: var(--purple); }
  .sdl-pbadge-mini[data-ring="green"]    { border-color: var(--green);    color: var(--green); }
  .sdl-pbadge-mini[data-ring="silver"]   { border-color: #d8d8e0;         color: #fff; }

  .sdl-pbadge-empty {
    background: rgba(255,255,255,0.025);
    border: 1.5px dashed rgba(255,255,255,0.22);
    color: rgba(255,255,255,0.3);
    font-size: 12px;
    font-weight: 700;
    cursor: default;
  }

  /* ---------- ROW VARIANT (player list cells) ---------- */
  .sdl-pbadge-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background: var(--concrete-light);
    border: 1px solid var(--glass-border);
    border-radius: 12px;
    transition: transform 0.15s ease, background 0.15s ease;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
  }
  .sdl-pbadge-row:active {
    transform: scale(0.98);
    background: var(--concrete-lighter);
  }
  /* Shrink the avatar wrap from card-size (64px) down to row-size (40px) */
  .sdl-pbadge-row .sdl-pbadge-avatar-wrap {
    width: 40px; height: 40px;
    margin: 0;
    flex-shrink: 0;
  }
  .sdl-pbadge-row .sdl-pbadge-avatar {
    font-size: 14px;
    border-width: 2px;
  }
  .sdl-pbadge-row .sdl-pbadge-ring { inset: -2px; }
  .sdl-pbadge-row .sdl-pbadge-dot {
    width: 11px; height: 11px;
    bottom: 0; right: 0;
    border-width: 2px;
  }

  .sdl-pbadge-row-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .sdl-pbadge-row-name {
    font-family: var(--font-display);
    font-size: 16px;
    letter-spacing: 1.5px;
    color: white;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sdl-pbadge-row-sub {
    font-family: var(--font-body);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: var(--gold);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sdl-pbadge-row-sub.cold { color: rgba(255,255,255,0.4); }

  /* Pill is shared between card and row variants */
  .sdl-pbadge-row > .sdl-pbadge-pill { flex-shrink: 0; }

  ${RING_GRADIENTS}
`;
